import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'
import type { VersionMeta } from './types'

/** Restore points read per GET /v1/versions page. */
export const VERSION_PAGE = 50
/** Never chain more pages than this in one (re)load — a safety stop, not a product limit. */
const MAX_PAGES = 40
/** While the newest entry's restore point is not stored yet, re-read this often, this many times. */
export const AWAIT_POLL_MS = 2000
const AWAIT_POLLS = 15

export interface RoomVersions {
  /** The restore points read so far, newest first. `null` until read (or when they could not be). */
  versions: VersionMeta[] | null
  /** Another page may lie below the oldest one read. */
  hasOlder: boolean
  loadingOlder: boolean
  /** Read the next older page ("Show older"). */
  loadOlder: () => void
  /** Replace one version with what the server answered (a tag or comment change). */
  replace: (v: VersionMeta) => void
  /** Read the list again from the newest (after a flatten removed versions). */
  reload: () => void
}

/**
 * The room's restore points, paged (AB#241), read while `enabled` (the History modal is open).
 * Each (re)load reads pages until they reach back past `since` (the log's oldest entry, so every
 * entry's restore point is known) and past the oldest version already shown (so a reload, which
 * `refresh` triggers as the log grows, never takes the rows "Show older" revealed away).
 * `awaiting` is the newest entry's id: the store hook tags its restore point a moment after the entry
 * lands, so while none read carries it the hook re-reads every AWAIT_POLL_MS (a bounded number of
 * times) and the row's tag and comment enable as soon as it is stored.
 */
export function useRoomVersions(
  room: string | null | undefined,
  enabled: boolean,
  since: string | null,
  refresh: unknown,
  awaiting: string | null = null,
): RoomVersions {
  const [versions, setVersions] = useState<VersionMeta[] | null>(null)
  const [hasOlder, setHasOlder] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [poll, setPoll] = useState(0)
  const [reloads, setReloads] = useState(0)
  const polls = useRef(0)
  // The oldest version number shown so far, read by the reload without being one of its triggers.
  const depth = useRef<number | null>(null)
  const sinceMs = since ? Date.parse(since) : Infinity

  useEffect(() => {
    if (!enabled || !room) {
      setVersions(null)
      setHasOlder(false)
      depth.current = null
      return
    }
    let live = true
    void (async () => {
      const read: VersionMeta[] = []
      let more = true
      for (let i = 0; i < MAX_PAGES && more; i++) {
        const last = read[read.length - 1]
        const page = (await api.versions(room, { ...(last ? { before: last.version } : {}), limit: VERSION_PAGE }))
          .versions
        read.push(...page)
        more = page.length === VERSION_PAGE
        const oldest = read[read.length - 1]
        if (!oldest) break
        const coversLog = Date.parse(oldest.createdAt) < sinceMs
        const coversShown = depth.current === null || oldest.version <= depth.current
        if (coversLog && coversShown) break
      }
      if (!live) return
      setVersions(read)
      setHasOlder(more)
      depth.current = read[read.length - 1]?.version ?? null
    })().catch(() => {
      if (live) setVersions(null)
    })
    return () => {
      live = false
    }
  }, [enabled, room, sinceMs, refresh, poll, reloads])

  // A new entry to wait for starts a new round of polls.
  useEffect(() => {
    polls.current = 0
  }, [awaiting])

  const missing = Boolean(enabled && awaiting && versions && !versions.some((v) => v.historyId === awaiting))
  useEffect(() => {
    if (!missing || polls.current >= AWAIT_POLLS) return
    const timer = setTimeout(() => {
      polls.current += 1
      setPoll((n) => n + 1)
    }, AWAIT_POLL_MS)
    return () => clearTimeout(timer)
  }, [missing, versions])

  const loadOlder = useCallback(() => {
    const oldest = versions?.[versions.length - 1]
    if (!room || !oldest || loadingOlder) return
    setLoadingOlder(true)
    api
      .versions(room, { before: oldest.version, limit: VERSION_PAGE })
      .then((res) => {
        setVersions((prev) => [...(prev ?? []), ...res.versions.filter((v) => v.version < oldest.version)])
        setHasOlder(res.versions.length === VERSION_PAGE)
        depth.current = res.versions[res.versions.length - 1]?.version ?? depth.current
      })
      .catch(() => undefined)
      .finally(() => setLoadingOlder(false))
  }, [room, versions, loadingOlder])

  const replace = useCallback((v: VersionMeta) => {
    setVersions((prev) => prev?.map((x) => (x.version === v.version ? v : x)) ?? prev)
  }, [])

  const reload = useCallback(() => setReloads((n) => n + 1), [])

  return { versions, hasOlder, loadingOlder, loadOlder, replace, reload }
}
